import { DateTimeFormatter, Duration, LocalTime } from "@js-joda/core"
import { Locale } from "@js-joda/locale"
import { range } from "lodash"

const timeFormatter = DateTimeFormatter.ofPattern("hh:mm a").withLocale(
  Locale.US
)

export const generateTimesOfDay = ({ step }: { step: Duration }): string[] => {
  const start = LocalTime.MIDNIGHT
  const totalSteps = Math.floor(Duration.ofHours(24).toNanos() / step.toNanos())

  return range(0, totalSteps)
    .map((i) => start.plus(step.multipliedBy(i)))
    .map((time) => time.format(timeFormatter))
}

export const isValidTime = (timeString: string): boolean => {
  // Regular expression for strict 12-hour format H:MM AM/PM
  const regex12Hour = /^(0?[1-9]|1[0-2]):[0-5]\d\s(AM|PM)$/i
  return regex12Hour.test(timeString)
}

const isDigit = (char: string) => /^\d$/.test(char)

const countDigits = (string: string) => string.split("").filter(isDigit).length

export const sanitizeTimeString = (timeString: string) => {
  const chars = timeString.toLowerCase().split("")
  let result = ""

  for (const char of chars) {
    const numberOfDigits = countDigits(result)
    if (isDigit(char) && numberOfDigits < 4) {
      result = result.concat(char)
    } else if (char === ":" && !result.includes(":") && numberOfDigits >= 1) {
      result = result.concat(char)
    } else if ((char === "a" || char === "p") && numberOfDigits >= 1) {
      result = result.concat(char)
      break
    }
  }

  return result
}

export const parseAndFormatTime = (input: string) => {
  const sanitized = sanitizeTimeString(input)

  // Step 1: Filter out alphabetic letters except for the last character (AM/PM indicator)
  const timeOnly =
    insertTimeSeparatorIfNeeded(sanitized.replace(/[a-zA-Z]/g, "")) ?? ""
  const lastCharacter = sanitized.slice(-1).toLowerCase()

  // Step 2: Convert time components to numbers (default to 0 if empty)
  const [hours, minutes] = timeOnly
    .split(":")
    .map((timeComponent) => parseInt(timeComponent || "0"))

  // Step 3: Validate hours and minutes
  if (hours > 12 || minutes > 59) {
    return null
  }

  // Step 4: Determine AM/PM based on last character or make an educated guess
  const isPM =
    lastCharacter === "p"
      ? true
      : lastCharacter === "a"
        ? false
        : guessPm(hours)

  // Step 5: Format output as "hh:mm a"
  const adjustedHours = isPM
    ? (hours % 12) + 12 // Add 12 for PM, but keep 12 PM as 12
    : hours % 12
  const localTime = LocalTime.of(adjustedHours, minutes)

  return localTime.format(timeFormatter)
}

const guessPm = (hours: number) => {
  return hours === 12 || (hours >= 1 && hours <= 6)
}

const insertTimeSeparatorIfNeeded = (timeString: string): string | null => {
  const len = timeString.length

  if (timeString.includes(":")) {
    return timeString
  } else if (len <= 2) {
    return timeString + ":" // Case 1: for 1 or 2 chars, like "1" -> "1:" or "10" -> "10:"
  } else {
    // Case 2: for 3 or 4 chars, like "100" -> "1:00" or "1000" -> "10:00"
    return timeString.slice(0, -2) + ":" + timeString.slice(-2)
  }
}
